'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TrustIndicator {
  id: number;
  type: 'certification' | 'partnership' | 'research';
  title: string;
  subtitle: string;
  icon: string;
}

interface TrustIndicatorsProps {
  className?: string;
}

const TrustIndicators = ({ className = '' }: TrustIndicatorsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const indicators: TrustIndicator[] = [
    {
      id: 1,
      type: 'certification',
      title: 'CE Certified',
      subtitle: 'European medical device compliance',
      icon: 'CheckBadgeIcon'
    },
    {
      id: 2,
      type: 'partnership',
      title: 'Gaia Healers Research',
      subtitle: 'Biofield measurement collaboration',
      icon: 'AcademicCapIcon'
    },
    {
      id: 3,
      type: 'research',
      title: '50+ Studies',
      subtitle: 'Peer-reviewed publications',
      icon: 'DocumentTextIcon'
    },
    {
      id: 4,
      type: 'certification',
      title: 'ISO 13485',
      subtitle: 'Quality management certified',
      icon: 'ShieldCheckIcon'
    },
    {
      id: 5,
      type: 'partnership',
      title: 'Quantum University Collaboration',
      subtitle: 'Consciousness research program',
      icon: 'BeakerIcon'
    },
    {
      id: 6,
      type: 'research',
      title: '15+ Countries',
      subtitle: 'Global research network',
      icon: 'GlobeAltIcon'
    }
  ];

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % indicators.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isHydrated, indicators.length]);

  if (!isHydrated) {
    return (
      <section data-component-id="src/app/homepage/components/TrustIndicators.tsx:83:6" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="83" data-component-file="TrustIndicators.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D" className={`bg-surface py-12 ${className}`}>
        <div data-component-id="src/app/homepage/components/TrustIndicators.tsx:84:8" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="84" data-component-file="TrustIndicators.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-component-id="src/app/homepage/components/TrustIndicators.tsx:85:10" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="85" data-component-file="TrustIndicators.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-8%22%7D" className="text-center mb-8">
            <h2 data-component-id="src/app/homepage/components/TrustIndicators.tsx:86:12" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="86" data-component-file="TrustIndicators.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20sm%3Atext-3xl%20font-bold%20text-primary%20mb-2%22%2C%22textContent%22%3A%22Trusted%20by%20Leading%20Institutions%22%7D" className="text-2xl sm:text-3xl font-bold text-primary mb-2">
              Trusted by Leading Institutions
            </h2>
            <p data-component-id="src/app/homepage/components/TrustIndicators.tsx:89:12" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="89" data-component-file="TrustIndicators.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%22%2C%22textContent%22%3A%22Scientific%20validation%20through%20global%20partnerships%22%7D" className="text-text-secondary">
              Scientific validation through global partnerships
            </p>
          </div>
          <div data-component-id="src/app/homepage/components/TrustIndicators.tsx:93:10" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="93" data-component-file="TrustIndicators.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20md%3Agrid-cols-3%20lg%3Agrid-cols-6%20gap-4%22%7D" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div data-component-id="src/app/homepage/components/TrustIndicators.tsx:95:14" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="95" data-component-file="TrustIndicators.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-lg%20p-6%20shadow-brand-sm%20h-32%22%7D" key={i} className="bg-white rounded-lg p-6 shadow-brand-sm h-32"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-component-id="src/app/homepage/components/TrustIndicators.tsx:104:4" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="104" data-component-file="TrustIndicators.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D" className={`bg-surface py-12 ${className}`}>
      <div data-component-id="src/app/homepage/components/TrustIndicators.tsx:105:6" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="105" data-component-file="TrustIndicators.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div data-component-id="src/app/homepage/components/TrustIndicators.tsx:106:8" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="106" data-component-file="TrustIndicators.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-8%22%7D" className="text-center mb-8">
          <h2 data-component-id="src/app/homepage/components/TrustIndicators.tsx:107:10" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="107" data-component-file="TrustIndicators.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20sm%3Atext-3xl%20font-bold%20text-primary%20mb-2%22%2C%22textContent%22%3A%22Trusted%20by%20Leading%20Institutions%22%7D" className="text-2xl sm:text-3xl font-bold text-primary mb-2">
            Trusted by Leading Institutions
          </h2>
          <p data-component-id="src/app/homepage/components/TrustIndicators.tsx:110:10" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="110" data-component-file="TrustIndicators.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%22%2C%22textContent%22%3A%22Scientific%20validation%20through%20global%20partnerships%22%7D" className="text-text-secondary">
            Scientific validation through global partnerships
          </p>
        </div>

        <div data-component-id="src/app/homepage/components/TrustIndicators.tsx:115:8" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="115" data-component-file="TrustIndicators.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20md%3Agrid-cols-3%20lg%3Agrid-cols-6%20gap-4%22%7D" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {indicators.map((indicator, index) => (
            <div data-component-id="src/app/homepage/components/TrustIndicators.tsx:117:12" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="117" data-component-file="TrustIndicators.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={indicator.id}
              className={`bg-white rounded-lg p-6 shadow-brand-sm transition-all duration-500 hover:shadow-brand-md hover:-translate-y-1 ${
                currentIndex === index ? 'ring-2 ring-accent' : ''
              }`}
            >
              <div data-component-id="src/app/homepage/components/TrustIndicators.tsx:123:14" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="123" data-component-file="TrustIndicators.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20text-center%22%7D" className="flex flex-col items-center text-center">
                <div data-component-id="src/app/homepage/components/TrustIndicators.tsx:124:16" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="124" data-component-file="TrustIndicators.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                  indicator.type === 'certification' ? 'bg-success/10' :
                  indicator.type === 'partnership'? 'bg-accent/10' : 'bg-primary/10'
                }`}>
                  <Icon data-component-id="src/app/homepage/components/TrustIndicators.tsx:128:18" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="128" data-component-file="TrustIndicators.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D"
                    name={indicator.icon as any}
                    size={24}
                    variant="solid"
                    className={
                      indicator.type === 'certification' ? 'text-success' :
                      indicator.type === 'partnership'? 'text-accent' : 'text-primary'
                    }
                  />
                </div>
                <h3 data-component-id="src/app/homepage/components/TrustIndicators.tsx:138:16" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="138" data-component-file="TrustIndicators.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-sm%20font-bold%20text-primary%20mb-1%22%7D" className="text-sm font-bold text-primary mb-1">
                  {indicator.title}
                </h3>
                <p data-component-id="src/app/homepage/components/TrustIndicators.tsx:141:16" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="141" data-component-file="TrustIndicators.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-text-secondary%22%7D" className="text-xs text-text-secondary">
                  {indicator.subtitle}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div data-component-id="src/app/homepage/components/TrustIndicators.tsx:149:8" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="149" data-component-file="TrustIndicators.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20mt-6%20space-x-2%22%7D" className="flex justify-center mt-6 space-x-2">
          {indicators.map((_, index) => (
            <button data-component-id="src/app/homepage/components/TrustIndicators.tsx:151:12" data-component-path="src/app/homepage/components/TrustIndicators.tsx" data-component-line="151" data-component-file="TrustIndicators.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                currentIndex === index ? 'bg-accent w-8' : 'bg-border'
              }`}
              aria-label={`Go to indicator ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustIndicators;